import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Crown, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";

import { onboardingAgentExternalService } from "app/components/onboarding-agent/onboardingAgentExternalService";
import {
    getQuickSearchModalStyles,
    QUICK_SEARCH_BOT_STRIP_HEIGHT_PX,
    QUICK_SEARCH_MODAL_FIXED_HEIGHT_PX,
    QUICK_SEARCH_MODAL_WIDTH_DEFAULT_PX,
    QUICK_SEARCH_TAB_PANEL_SCROLL_MAX_HEIGHT_PX,
} from "app/components/React/QuickSearch/config";
import { quickSearchDataAutomation } from "app/components/React/QuickSearch/dataAutomation";
import { QUICK_SEARCH } from "app/components/React/QuickSearch/texts";
import type { SearchTabs } from "app/components/React/QuickSearch/types";
import { SearchAutocompleteWithTabs } from "app/components/React/SearchAutocompleteWithTabs/SearchAutocompleteWithTabs";
import { translate } from "app/filters/i18nFilter";
import { useToggle } from "app/pages/activation/hooks/useToggle";
import { openDefaultHooksModal } from "app/services/ModalService";
import { isMac } from "app/UtilitiesAndConstants/UtilityFunctions/osDetection";

import { useShortcut } from "./hooks/useShortCut";
import { QuickSearchBotStrip } from "./QuickSearchBotStrip/QuickSearchBotStrip";
import * as SC from "./styles";
import quickSearchTracking from "./tracking";

type QuickSearchProps = {
    isCollapsed: boolean;
    isLocked?: boolean;
};

export const QuickSearch = ({ isCollapsed, isLocked }: QuickSearchProps) => {
    const shortcutText = isMac() ? "⌘K" : "ctrl+k";
    const [isModalOpen, openModal, closeModal] = useToggle();
    const [isBotAvailable, setIsBotAvailable] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");

    const firstRender = useRef(true);
    const modalContentRef = useRef<HTMLDivElement>(null);

    const closeModalRef = useRef(closeModal);
    closeModalRef.current = closeModal;
    const stableCloseModal = useCallback(() => closeModalRef.current(), []);

    const modalStyles = useMemo(
        () =>
            getQuickSearchModalStyles(
                QUICK_SEARCH_MODAL_WIDTH_DEFAULT_PX,
                isBotAvailable ? QUICK_SEARCH_MODAL_FIXED_HEIGHT_PX + QUICK_SEARCH_BOT_STRIP_HEIGHT_PX : undefined,
            ),
        [isBotAvailable],
    );

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        if (isModalOpen) {
            const isRegistered = onboardingAgentExternalService.isRegistered();
            setIsBotAvailable(isRegistered);
            quickSearchTracking.trackSearchPopupOpen(isRegistered);
        } else {
            quickSearchTracking.trackSearchPopupClose();
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (!isModalOpen) {
            setSearchQuery("");
        }
    }, [isModalOpen]);

    const handleLockedClick = useCallback(() => {
        openDefaultHooksModal();
    }, []);

    useShortcut("k", isLocked ? handleLockedClick : openModal);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        const handleMouseDown = (e: MouseEvent) => {
            const node = modalContentRef.current;
            if (node && !node.contains(e.target as Node)) {
                stableCloseModal();
            }
        };

        document.addEventListener("mousedown", handleMouseDown, { passive: true });

        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
        };
    }, [isModalOpen, stableCloseModal]);

    const onBarClick = () => {
        if (isLocked) {
            handleLockedClick();
            return;
        }
        openModal();
        quickSearchTracking.trackClickOnSearchBar();
    };

    const onKeywordClick = (keyword?: string, state?: string) => {
        if (keyword) {
            quickSearchTracking.trackSearchItemClick("keyword", keyword, state);
        }
    };
    const onWebsiteClick = (website?: string, state?: string) => {
        if (website) {
            quickSearchTracking.trackSearchItemClick("website", website, state);
        }
    };
    const onReportClick = (title: string, state?: string) => {
        quickSearchTracking.trackSearchItemClick("report", title, state);
    };
    const onTabClick = (tab: SearchTabs) => {
        quickSearchTracking.trackSelectTab(tab);
    };

    const handleLiveSearchQueryChange = useCallback((query: string) => {
        setSearchQuery(query);
    }, []);

    const handleBotStripClick = useCallback(
        (query: string) => {
            quickSearchTracking.trackAIAssistantClick(query);
            stableCloseModal();
            onboardingAgentExternalService.openWithMessage(query || undefined);
        },
        [stableCloseModal],
    );

    const showBotStrip = isBotAvailable;

    return (
        <>
            <Modal
                isOpen={isModalOpen}
                customStyles={modalStyles}
                onRequestClose={closeModal}
                data-automation={quickSearchDataAutomation.modal}
            >
                <SC.ModalContent ref={modalContentRef}>
                    <SC.AutocompleteWrapper>
                        <SearchAutocompleteWithTabs
                            isOpen={isModalOpen}
                            onClose={stableCloseModal}
                            onWebsiteClick={onWebsiteClick}
                            onKeywordClick={onKeywordClick}
                            onReportClick={onReportClick}
                            onTabClick={onTabClick}
                            tabPanelScrollMaxHeightPx={QUICK_SEARCH_TAB_PANEL_SCROLL_MAX_HEIGHT_PX}
                            onLiveSearchQueryChange={handleLiveSearchQueryChange}
                        />
                    </SC.AutocompleteWrapper>
                    {showBotStrip ? (
                        <QuickSearchBotStrip
                            query={searchQuery}
                            onClick={handleBotStripClick}
                        />
                    ) : null}
                </SC.ModalContent>
            </Modal>

            <SC.Wrapper
                isCollapsed={isCollapsed}
                onClick={onBarClick}
                data-automation={quickSearchDataAutomation.searchBar}
            >
                <SWReactIcons
                    iconName="search"
                    size="xs"
                    iconColor={colorsPalettes.carbon[300]}
                />

                <SC.Text
                    isCollapsed={isCollapsed}
                    data-automation={quickSearchDataAutomation.searchBarText}
                >
                    {translate(QUICK_SEARCH)}
                </SC.Text>

                {isLocked ? (
                    <SC.LockIconWrapper
                        isCollapsed={isCollapsed}
                        data-automation={quickSearchDataAutomation.searchBarLock}
                    >
                        <Crown
                            size="xs"
                            color={colorsPalettes.carbon[400]}
                        />
                    </SC.LockIconWrapper>
                ) : (
                    <SC.SortCutWrapper
                        isCollapsed={isCollapsed}
                        data-automation={quickSearchDataAutomation.searchBarShortCut}
                    >
                        {shortcutText}
                    </SC.SortCutWrapper>
                )}
            </SC.Wrapper>
        </>
    );
};
